import React, { useState } from 'react';
import {
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
    useTransitionStyles,
} from '@floating-ui/react';
import { MdMoreHoriz, MdOutlineDriveFileRenameOutline, MdOutlineDeleteOutline } from 'react-icons/md';

interface IProps {
    onRename: () => void;
    onDelete: () => void;
}

export const ConversationItemMenu: React.FC<IProps> = ({ onRename, onDelete }) => {
    const [open, setOpen] = useState(false);

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: setOpen,
        placement: 'bottom-end',
        middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });

    const click = useClick(context, { event: 'mousedown' });
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'menu' });
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

    const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
        duration: { open: 180, close: 120 },
        initial: { opacity: 0, transform: 'translateY(-4px) scale(0.96)' },
        open: { opacity: 1, transform: 'translateY(0) scale(1)' },
    });

    // React events bubble через component tree (не DOM!), поэтому даже из FloatingPortal
    // click всплывает к onClick родительского <ConversationListItem>. Без stopPropagation
    // удаление активного треда тут же реактивирует его через handleSelect → setActiveConversation.
    const handleAction = (cb: () => void) => (e: React.MouseEvent) => {
        e.stopPropagation();
        setOpen(false);
        cb();
    };

    return (
        <>
            <button
                ref={refs.setReference}
                {...getReferenceProps({
                    onPointerDown: (e) => e.stopPropagation(),
                    onClick: (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                    },
                })}
                type="button"
                className="chat-ai-sidebar__item-menu-trigger"
                aria-label="Действия"
            >
                <MdMoreHoriz size={16} aria-hidden />
            </button>
            {isMounted && (
                <FloatingPortal>
                    <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 1200 }} {...getFloatingProps()}>
                        <div className="chat-ai-menu" style={transitionStyles}>
                            <button
                                type="button"
                                className="chat-ai-menu__item"
                                onClick={handleAction(onRename)}
                            >
                                <span className="chat-ai-menu__item-icon" aria-hidden>
                                    <MdOutlineDriveFileRenameOutline size={15} />
                                </span>
                                <span className="chat-ai-menu__item-label">Переименовать</span>
                                <span className="chat-ai-menu__item-shortcut" aria-hidden>F2</span>
                            </button>
                            <div className="chat-ai-menu__sep" aria-hidden />
                            <button
                                type="button"
                                className="chat-ai-menu__item chat-ai-menu__item--danger"
                                onClick={handleAction(onDelete)}
                            >
                                <span className="chat-ai-menu__item-icon" aria-hidden>
                                    <MdOutlineDeleteOutline size={15} />
                                </span>
                                <span className="chat-ai-menu__item-label">Удалить чат</span>
                            </button>
                        </div>
                    </div>
                </FloatingPortal>
            )}
        </>
    );
};
